import { Languages, TextInitial } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LanguageUseBoxSource } from '../../../../../../shared/grammar-rules-resolution.js';
import { cn } from '../../../../../lib/utils.js';
import type { ConfidenceLevel } from '../utils.js';

export type BoxMode = 'content' | 'languageUse';

/**
 * Per-box content/language-use toggle. Distinct from grading-view.tsx's whole-canvas
 * mode: 'answers'|'correction'|'grammar' toggle — this one only switches which language-use/content
 * data the GradingScheme/AI-grading panels display for the current task, never the whole canvas. When
 * a task belongs to more than one language-use group, switching between them happens via the chevrons
 * on the group badge in the panel body (see GrammarGroupNavBadge), not here.
 */
export function BoxModeToggle({
  boxMode,
  onChangeBoxMode,
  sources,
  contentDotLevel,
  languageUseDotLevel,
}: {
  boxMode: BoxMode;
  onChangeBoxMode: (mode: BoxMode) => void;
  sources: LanguageUseBoxSource[];
  contentDotLevel?: ConfidenceLevel;
  languageUseDotLevel?: ConfidenceLevel;
}) {
  const { t } = useTranslation();
  if (sources.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-secondary p-0.5 text-xs shadow-sm min-w-0">
        <button
          type="button"
          onClick={() => onChangeBoxMode('content')}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold transition-colors min-w-0',
            boxMode === 'content'
              ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
              : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300',
          )}
        >
          <TextInitial size={14} className="shrink-0" />
          <span className="hidden overflow-hidden text-ellipsis whitespace-nowrap @[22rem]:inline @[22rem]:min-w-[24px]">
            {t('Canvas.grading.boxModeContent')}
          </span>
          {contentDotLevel && contentDotLevel !== 'none' && (
            <span
              className={cn(
                'w-1.5 h-1.5 rounded-full shrink-0',
                contentDotLevel === 'low' ? 'bg-red-500' : 'bg-amber-500',
              )}
            />
          )}
        </button>
        <button
          type="button"
          onClick={() => onChangeBoxMode('languageUse')}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold transition-colors min-w-0',
            boxMode === 'languageUse'
              ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
              : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300',
          )}
        >
          <Languages size={14} className="shrink-0" />
          <span className="hidden overflow-hidden text-ellipsis whitespace-nowrap @[22rem]:inline @[22rem]:min-w-[24px]">
            {t('Canvas.grading.boxModeLanguageUse')}
          </span>
          {languageUseDotLevel && languageUseDotLevel !== 'none' && (
            <span
              className={cn(
                'w-1.5 h-1.5 rounded-full shrink-0',
                languageUseDotLevel === 'low' ? 'bg-red-500' : 'bg-amber-500',
              )}
            />
          )}
        </button>
      </div>
    </div>
  );
}
